import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, formatError } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { LOGO } from "@/lib/assets";
import { ArrowLeft } from "lucide-react";

export default function ResetPassword() {
  const { t } = useLang();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 6) return toast.error("Password must be at least 6 characters");
    if (pw !== pw2) return toast.error("Passwords do not match");
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      toast.success("Password updated. Please sign in.");
      navigate("/login");
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Invalid or expired link");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-icdh-navy flex items-center justify-center p-6">
      <div className="noise-overlay fixed inset-0 opacity-[0.04] pointer-events-none" />
      <div className="relative w-full max-w-md bg-icdh-white p-10 md:p-12">
        <Link to="/" className="flex items-center gap-3 mb-10">
          <img src={LOGO} alt="ICDH" className="h-12 w-12 object-contain" />
          <div>
            <p className="font-extrabold tracking-tight text-base text-icdh-navy">Institution Chrétienne d'Haïti</p>
            <p className="font-serif italic text-icdh-red text-sm">{t.motto}</p>
          </div>
        </Link>
        <h1 className="font-serif text-3xl tracking-tight text-icdh-navy">Set a new password</h1>
        <p className="mt-2 text-icdh-navy/60 text-sm">Choose a new password below. The reset link is single-use.</p>

        {!token ? (
          <p className="mt-8 text-icdh-red text-sm">Missing token in URL. Use the link from your email.</p>
        ) : (
          <form onSubmit={submit} data-testid="reset-form" className="mt-8 space-y-6">
            <input required type="password" data-testid="reset-pw" placeholder="New password" value={pw} onChange={(e) => setPw(e.target.value)}
              className="w-full h-12 border-b border-icdh-navy/25 focus:border-icdh-red outline-none bg-transparent text-icdh-navy" />
            <input required type="password" data-testid="reset-pw2" placeholder="Confirm new password" value={pw2} onChange={(e) => setPw2(e.target.value)}
              className="w-full h-12 border-b border-icdh-navy/25 focus:border-icdh-red outline-none bg-transparent text-icdh-navy" />
            <button type="submit" disabled={loading} data-testid="reset-submit"
              className="w-full h-14 bg-icdh-red text-icdh-white text-xs font-bold uppercase tracking-[0.15em] hover:bg-icdh-navy transition-colors duration-300 disabled:opacity-60">
              {loading ? "..." : "Update password"}
            </button>
          </form>
        )}

        <Link to="/login" className="mt-8 inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-icdh-navy/60 hover:text-icdh-red transition-colors">
          <ArrowLeft size={14} />Back to login
        </Link>
      </div>
    </div>
  );
}
